import React, { useState } from 'react'
import { addDoc, collection } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'

const CERT_TYPES = ['OSHA Training', 'First Aid/CPR', 'Equipment Certification', 'Safety Certificate', 'Professional License', 'Other']
const PROJECTS = ['Project Alpha', 'Project Beta', 'Project Gamma']

interface Props {
  onClose: () => void
  onCreated: () => void
}

export default function CertificationModal({ onClose, onCreated }: Props) {
  const { orgId, userData } = useAuth()
  const [name, setName] = useState('')
  const [userName, setUserName] = useState('')
  const [certType, setCertType] = useState(CERT_TYPES[0])
  const [projectName, setProjectName] = useState(PROJECTS[0])
  const [issueDate, setIssueDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) {
      setError('orgId is missing — please log out and back in')
      return
    }
    setSaving(true)
    setError('')
    try {
      await addDoc(collection(db, 'organizations', orgId, 'certifications'), {
        orgId,
        name,
        userName,
        certType,
        projectName,
        issueDate,
        expiryDate,
        notes,
        createdByName: userData?.name || userData?.email || 'unknown',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      onCreated()
      onClose()
    } catch (err: any) {
      console.error('CertificationModal error', err)
      setError(err?.message || 'Failed to add certification. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add Certification</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certification Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
                placeholder="e.g., OSHA 30" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Holder Name</label>
              <input type="text" value={userName} onChange={e => setUserName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
                placeholder="Who holds this cert" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={certType} onChange={e => setCertType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {CERT_TYPES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={projectName} onChange={e => setProjectName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {PROJECTS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
              <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
              rows={2} placeholder="Additional details" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
              {saving ? 'Saving...' : 'Add Certification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
